import type { ApplyWebPatchAction, MergePlatformAction, OverwriteStoreAction } from "../../types/actionTypes";
import type { WrpfSessionAction } from "./wrpfSessionActions";
import { defaultWrpfSessionState } from "./wrpfSessionActions";
import type { WrpfBundleMeta, WrpfSessionState } from "./types";

type Action = WrpfSessionAction | OverwriteStoreAction | MergePlatformAction | ApplyWebPatchAction | { type: string };

export default function wrpfSessionReducer(
  state: WrpfSessionState = defaultWrpfSessionState(),
  action: Action,
): WrpfSessionState {
  switch (action.type) {
    case "OVERWRITE_STORE": {
      const overwrite = action as OverwriteStoreAction;
      const incoming = overwrite.store.wrpfSession;

      // Un bundle WRPF real / handoff fresco trae un token usable → adoptarlo.
      if (incoming?.callbackToken) {
        return incoming;
      }

      // Cargar un archivo SIN token usable (vanilla, o un export propio donde "Save to File" saneó
      // callbackToken a null) NO debe desconectar la sesión activa: conservamos tournamentId/callback/
      // token para seguir publicando resultados anclados a ESTE torneo. Soltamos entryAnchors porque
      // los ids de entrada del archivo no corresponden a los anchors del bundle → match por DNI.
      // Excepción: si el archivo declara OTRO torneo, no corresponde mantener esta conexión.
      const sameOrUnknownTournament =
        !incoming?.tournamentId || incoming.tournamentId === state.tournamentId;
      // Excepción a la excepción: un archivo guardado del MISMO torneo trae los anchors de SUS entries
      // (el merge de tarimas re-numera ids y los anchors viajan con ellos). Sin ellos se pierden los
      // récords, la publicación anclada y "Traer cambios de la web".
      if (state.tournamentId && state.callbackToken && sameOrUnknownTournament) {
        const fileAnchors = incoming?.tournamentId === state.tournamentId ? incoming.entryAnchors ?? [] : [];
        return { ...state, entryAnchors: fileAnchors };
      }

      // Sin sesión activa (o archivo de otro torneo) → usar la del archivo o el default. "Guardar como"
      // sanea wrpfSession.callbackToken pero deja la sesión con la que se guardó en _wrpf.callback:
      // recuperarla mantiene "Enviar resultados a la web". Si la web ya la revocó, el envío lo avisa.
      const fileMeta = (overwrite.store as { _wrpf?: Pick<WrpfBundleMeta, "tournamentId" | "callback"> })._wrpf;
      const fileCallback = fileMeta?.callback;
      if (incoming?.tournamentId && fileMeta?.tournamentId === incoming.tournamentId && fileCallback?.token) {
        return {
          ...incoming,
          callbackToken: fileCallback.token,
          callbackUrl: incoming.callbackUrl ?? fileCallback.resultsUrl,
          tokenExpiresAt: fileCallback.expiresAt || incoming.tokenExpiresAt,
        };
      }
      return incoming ?? defaultWrpfSessionState();
    }

    case "SET_WRPF_SESSION": {
      const setSession = action as Extract<WrpfSessionAction, { type: "SET_WRPF_SESSION" }>;
      const next = setSession.session;
      // El alta por handshake aplica el bundle (con la base de "Traer cambios de la web") y
      // enseguida re-setea la sesión con el token del link: la base del mismo torneo se conserva.
      if (next.webBaseline === undefined && state.webBaseline && next.tournamentId === state.tournamentId) {
        return { ...next, webBaseline: state.webBaseline };
      }
      return next;
    }

    case "UPDATE_WRPF_TOKEN": {
      const update = action as Extract<WrpfSessionAction, { type: "UPDATE_WRPF_TOKEN" }>;
      return {
        ...state,
        callbackToken: update.callbackToken,
        tokenExpiresAt: update.tokenExpiresAt,
        callbackUrl: update.callbackUrl,
      };
    }

    case "SET_WRPF_LAST_DOWNLOADED": {
      const downloaded = action as Extract<WrpfSessionAction, { type: "SET_WRPF_LAST_DOWNLOADED" }>;
      return { ...state, lastDownloadedAt: downloaded.at };
    }

    case "SET_WRPF_LAST_PUBLISHED": {
      const published = action as Extract<WrpfSessionAction, { type: "SET_WRPF_LAST_PUBLISHED" }>;
      return { ...state, lastResultsPublishedAt: published.at };
    }

    // Merge re-keys incoming entries; carry their anchors so published results keep userOnTournamentId.
    case "MERGE_PLATFORM": {
      const merge = action as MergePlatformAction;
      return { ...state, entryAnchors: merge.anchors };
    }

    // Todo lo procesado en "Traer cambios de la web" (aplicado o rechazado) pasa a ser la base.
    case "APPLY_WEB_PATCH": {
      const patch = action as ApplyWebPatchAction;
      const webBaseline = { ...(state.webBaseline ?? {}) };
      for (const [identity, values] of Object.entries(patch.baseline)) {
        webBaseline[identity] = { ...(webBaseline[identity] ?? {}), ...values };
      }
      return { ...state, webBaseline };
    }

    default:
      return state;
  }
}
